import { storeId } from '@/stores/storeId'
import { defineStore } from 'pinia'

import { isCartBundleNotFoundError, isCartUpsertConcurrencyError } from '@/lib/cartErrors'
import { CADY_SCENE_ID_ATTR } from '@/lib/cartItemAttributes'
import { findSceneIncompatibleAddOnBundleIds } from '@/lib/sessionCatalog'
import { appAnalyticsService } from '@/services/AppAnalyticsService'
import Sunny16CommerceCartService from '@/services/Sunny16CommerceCartService'
import Sunny16CommerceCheckoutService from '@/services/Sunny16CommerceCheckoutService'
import Sunny16CommerceOrderService from '@/services/Sunny16CommerceOrderService'

import type {
  CartBundleStorefrontDto,
  CartItemAppointmentCustomizationDto,
  CartItemUpsertStorefrontDto,
  CartStorefrontDto,
  CartStorefrontValidationResultDto,
  CheckoutDetailsDto,
  PromoCodeResultStorefrontDto
} from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'
import {
  ApplyPromoCodeStorefrontDto,
  CartBundleUpsertStorefrontDto
} from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'

const sunny16CommerceCartService = new Sunny16CommerceCartService()
const sunny16CommerceCheckoutService = new Sunny16CommerceCheckoutService()
const sunny16CommerceOrderService = new Sunny16CommerceOrderService()

interface CartStoreState {
  cartData: CartStorefrontDto | null
}

export const useCartStore = defineStore(storeId.CART, {
  state: (): CartStoreState => ({
    cartData: null
  }),
  getters: {
    cartId(state: CartStoreState): string | undefined {
      return state.cartData?.cartId
    },
    cartSecurityKey(state: CartStoreState): string | undefined {
      return state.cartData?.securityKey
    },
    cartHasBundles(state: CartStoreState): boolean {
      return (state.cartData?.cartBundles?.length ?? 0) > 0
    },
    cartBundles(state: CartStoreState): CartBundleStorefrontDto[] {
      return state.cartData?.cartBundles ?? []
    },
    /** Sum of `bundleTotalPostDiscounts` across all cart bundles — used as the GA4 `value` for purchase/cart events. */
    cartTotal(state: CartStoreState): number {
      return (state.cartData?.cartBundles ?? []).reduce(
        (sum, cb) => sum + cb.bundleTotalPostDiscounts,
        0
      )
    },
    /**
     * Deduped CadyScene enum values for every cart item carrying a `CADY_SceneId`
     * attribute, across all cart bundles. Source of truth for the scenes bound
     * to the user's session — covers both user-selected and bundle-included scenes.
     */
    cadySceneEnumValuesInCart(state: CartStoreState): number[] {
      const enumValues = new Set<number>()
      for (const cartBundle of state.cartData?.cartBundles ?? []) {
        for (const cartItem of cartBundle.cartItems) {
          const attr = cartItem.item?.attributes?.find((a) => a.key === CADY_SCENE_ID_ATTR)
          if (!attr) continue
          const parsed = Number(attr.value)
          if (!Number.isNaN(parsed)) enumValues.add(parsed)
        }
      }
      return [...enumValues]
    },
    catalogBundleIsInCart(state: CartStoreState) {
      return (catalogBundleId: string) =>
        state.cartData?.cartBundles
          .map((cb: CartBundleStorefrontDto) => cb.catalogBundleId)
          .includes(catalogBundleId) ?? false
    },
    getCartBundleByCatalogBundleId(state: CartStoreState) {
      return (catalogBundleId: string) =>
        state.cartData?.cartBundles.find((cb) => cb.catalogBundleId === catalogBundleId) ?? null
    },
    orderId(state: CartStoreState): string | null {
      return state.cartData?.order?.orderId ?? null
    },
    /**
     * True when the cart's authoritative post-discount total is exactly $0.
     * Reads `order.orderTotals.total` (post-everything) with `subtotalPostDiscounts` as the
     * pre-totals fallback. Do NOT derive this from booking-store base prices — those exclude
     * promo discounts by design, so a 100%-off promo on a $50 cart would falsely report
     * not-zero and the no-charge / deferred-intent branches would never engage.
     */
    isZeroTotal(state: CartStoreState): boolean {
      const totals = state.cartData?.order?.orderTotals
      if (!totals) return false
      const effectiveTotal = totals.total ?? totals.subtotalPostDiscounts
      return effectiveTotal === 0
    },
    clientSecret(state: CartStoreState): string | null {
      const transactions = state.cartData?.order?.orderTransactions
      if (!transactions || transactions.length === 0) return null
      // Mid-checkout promo flips can leave canceled Stripe transactions in the array; mirror the backend's `FindOpenStripeOrderTransaction` filter so we only surface the clientSecret of an actionable PaymentIntent.
      for (let i = transactions.length - 1; i >= 0; i--) {
        const details = transactions[i].stripePaymentDetails
        if (details && details.status !== 'canceled' && details.clientSecret) {
          return details.clientSecret
        }
      }
      return null
    }
  },
  actions: {
    /**
     * Upsert a cart bundle by catalog bundle ID. Builds the DTO internally.
     * Creates the cart on first call (no cartId yet); subsequent calls pass
     * the stored cartId/securityKey. If a bundle with the same catalogBundleId
     * already exists in the cart, the existing cartBundleId is passed to update
     * rather than create a duplicate.
     *
     * @param catalogBundleId - The catalog bundle to upsert.
     * @param options.selectedItems - Item group item selections (e.g., scenes, hair/makeup).
     * @param options.quantity - Bundle quantity (default 1).
     */
    async upsertCartBundle(
      catalogBundleId: string,
      options?: {
        selectedItems?: CartItemUpsertStorefrontDto[]
        quantity?: number
        subjectId?: number | null
      }
    ) {
      const dto = new CartBundleUpsertStorefrontDto()
      dto.catalogBundleId = catalogBundleId
      dto.quantity = options?.quantity ?? 1
      if (options?.selectedItems) {
        dto.selectedItems = options.selectedItems
      }
      // If a bundle with this catalog ID already exists, pass the cartBundleId to update it
      const existing = this.getCartBundleByCatalogBundleId(catalogBundleId)
      if (existing) {
        dto.cartBundleId = existing.cartBundleId
      }
      try {
        this.cartData = await sunny16CommerceCartService.upsertCartBundle(
          dto,
          this.cartId,
          this.cartSecurityKey,
          options?.subjectId ?? null
        )
      } catch (error) {
        if (isCartUpsertConcurrencyError(error)) return
        throw error
      }
    },
    /**
     * Composite action for session-tier bundle swaps (upgrade, downgrade, or
     * lateral). Replaces the old session bundle with a new one and removes any
     * cart bundles whose catalogBundleId is not valid for the new tier.
     *
     * Two-layer reconciliation:
     * 1. Upsert the new session bundle (creates cart if none exists).
     * 2. Catalog-id sweep — remove any cart bundle whose catalogBundleId is
     *    not in the valid set (new session bundle + its add-ons, prepays,
     *    cart upsells).
     * 3. Scene-aware sweep (when `allowedSceneIds` is provided) — remove
     *    any non-session cart bundle whose `cartItems` reference a scene
     *    outside the allowed set. Catches scene-shaped add-ons whose
     *    catalog id sits in both variants' valid sets, which Layer 1
     *    misses.
     *
     * @param newCatalogBundleId - Catalog bundle ID for the new session tier.
     * @param validCatalogBundleIds - All catalog bundle IDs valid for the new tier
     *   (add-ons, prepays, cart upsells). The new session bundle ID is included automatically.
     * @param options.selectedItems - Scene/item selections for the new bundle.
     * @param options.allowedSceneIds - Optional scene-id set permitted in the
     *   new context (e.g. `ROADSHOW_COMPATIBLE_SCENE_ID_SET` for RS+ targets).
     *   When omitted, the scene-aware sweep is skipped and only Layer 1 runs.
     */
    async performBundleSwapCartSequence(
      newCatalogBundleId: string,
      validCatalogBundleIds: string[],
      options?: {
        selectedItems?: CartItemUpsertStorefrontDto[]
        allowedSceneIds?: ReadonlySet<string>
      }
    ): Promise<void> {
      await this.upsertCartBundle(newCatalogBundleId, {
        selectedItems: options?.selectedItems
      })
      const validSet = new Set([newCatalogBundleId, ...validCatalogBundleIds])
      const toRemove = this.cartBundles.filter((cb) => !validSet.has(cb.catalogBundleId))
      for (const bundle of toRemove) {
        await this.removeCartBundle(bundle.cartBundleId)
      }
      if (options?.allowedSceneIds) {
        const sceneOrphans = findSceneIncompatibleAddOnBundleIds(
          this.cartBundles,
          newCatalogBundleId,
          options.allowedSceneIds
        )
        for (const cartBundleId of sceneOrphans) {
          await this.removeCartBundle(cartBundleId)
        }
      }
    },
    async updateCartIdentifiers(subjectId: number, eventIdentifier?: number | null): Promise<void> {
      this.cartData = await sunny16CommerceCartService.updateCartIdentifiers(
        this.cartId as string,
        this.cartSecurityKey as string,
        subjectId,
        eventIdentifier
      )
    },
    /**
     * Find and remove non-session cart bundles whose `cartItems` reference
     * a scene outside the allowed set — e.g. a studio-only Outdoor add-on
     * persisted at a school booking. Returns the count so callers can
     * decide whether to surface a "cart updated" toast or processing UI;
     * a no-op (no cart bundles, or nothing scene-incompatible) returns
     * `{ removedCount: 0 }` without doing any work.
     */
    async sweepSceneIncompatibleBundles(
      sessionCatalogBundleId: string,
      allowedSceneIds: ReadonlySet<string>
    ): Promise<{ removedCount: number }> {
      if (!this.cartHasBundles) return { removedCount: 0 }
      const orphanIds = findSceneIncompatibleAddOnBundleIds(
        this.cartBundles,
        sessionCatalogBundleId,
        allowedSceneIds
      )
      for (const id of orphanIds) {
        await this.removeCartBundle(id)
      }
      return { removedCount: orphanIds.length }
    },
    async removeCartBundle(cartBundleId: string) {
      try {
        this.cartData = await sunny16CommerceCartService.deleteCartBundle(
          this.cartId as string,
          this.cartSecurityKey as string,
          cartBundleId
        )
      } catch (error) {
        if (isCartBundleNotFoundError(error)) return
        throw error
      }
    },
    /**
     * Remove a cart bundle by its catalog bundle ID.
     * Assumes 1:1 relationship between catalog bundles and cart bundles.
     */
    async removeCartBundleByCatalogBundleId(catalogBundleId: string) {
      const cartBundle = this.cartBundles.find((cb) => cb.catalogBundleId === catalogBundleId)
      if (!cartBundle) return
      try {
        this.cartData = await sunny16CommerceCartService.deleteCartBundle(
          this.cartId as string,
          this.cartSecurityKey as string,
          cartBundle.cartBundleId
        )
      } catch (error) {
        if (isCartBundleNotFoundError(error)) return
        throw error
      }
      appAnalyticsService.trackRemoveFromCart(cartBundle)
    },
    async upsertCartItemCustomizations(
      cartItemCustomizations: CartItemAppointmentCustomizationDto[]
    ) {
      this.cartData = await sunny16CommerceCartService.upsertCartCustomizations(
        this.cartId as string,
        this.cartSecurityKey as string,
        cartItemCustomizations
      )
    },
    async applyPromoCode(promoCode: string): Promise<Partial<PromoCodeResultStorefrontDto>> {
      const dataRequest = new ApplyPromoCodeStorefrontDto()
      dataRequest.code = promoCode
      try {
        const response = await sunny16CommerceCartService.applyPromoCode(
          this.cartId as string,
          this.cartSecurityKey as string,
          dataRequest
        )
        if (response && response.isApplied) {
          this.cartData = response.cart
        }
        return {
          isApplied: response.isApplied,
          message: response.message
        }
      } catch (error) {
        console.error(`Error applying promo code: ${promoCode}`, error)
      }
      return {
        isApplied: false,
        message: `Error applying promo code: ${promoCode}`
      }
    },
    async prepareCart() {
      this.cartData = await sunny16CommerceCartService.prepareCart(
        this.cartId as string,
        this.cartSecurityKey as string
      )
    },
    async validateCart(): Promise<CartStorefrontValidationResultDto> {
      return await sunny16CommerceCartService.validateCart(
        this.cartId as string,
        this.cartSecurityKey as string
      )
    },
    /**
     * Convert the cart to an order. Called after successful payment.
     */
    async upsertOrder(checkoutDetails: CheckoutDetailsDto): Promise<CartStorefrontDto> {
      const response = await sunny16CommerceOrderService.upsertOrder(
        this.cartId as string,
        checkoutDetails
      )
      this.cartData = response
      return response
    },
    async getCart(): Promise<void> {
      this.cartData = await sunny16CommerceCartService.getCart(
        this.cartId as string,
        this.cartSecurityKey as string
      )
    },
    /**
     * Creates or updates the Stripe PaymentIntent for the current order.
     * First call creates the PaymentIntent; subsequent calls update the amount.
     * After this call, `clientSecret` getter returns the PaymentIntent client secret.
     */
    async initStripePaymentElement(): Promise<void> {
      this.cartData = await sunny16CommerceCheckoutService.initStripePaymentElement(
        this.orderId as string
      )
    },
    /**
     * Submit a $0 order for finalization without going through Stripe payment confirmation.
     * Mirrors what the Stripe `confirmPayment` flow does for paid carts: locks the cart
     * server-side and returns the finalized cart payload. The endpoint takes no body — the
     * order's customer fields must already be persisted via a prior `UpsertOrder` call.
     */
    async completeFreeOrder(): Promise<{ success: boolean; message?: string }> {
      try {
        const response = await sunny16CommerceCheckoutService.completeFreeOrder(
          this.orderId as string
        )
        this.cartData = response
        return { success: true }
      } catch (error) {
        console.error('[cartStore.completeFreeOrder] Failed to complete free order:', error)
        return {
          success: false,
          message: error instanceof Error ? error.message : 'Failed to complete free order.'
        }
      }
    },
    resetStore(): void {
      this.cartData = null
    }
  },
  persist: true
})
